import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

interface ChatRoom {
  id: string;
  book_request_id: string;
  requester_id: string;
  owner_id: string;
  is_active: boolean;
  created_at: string;
  closed_at?: string;
  book_requests?: {
    id: string;
    status: string;
    books?: {
      title: string;
      author: string;
      cover_image?: string;
    };
  };
  requester?: {
    id: string;
    name: string;
  };
  owner?: {
    id: string;
    name: string;
  };
  unread_count?: number;
}

interface ChatEligibility {
  hasActiveChat: boolean;
  chatRooms: ChatRoom[];
  isLoading: boolean;
  error: string | null;
}

export const useChatEligibility = () => {
  const { user, token } = useAuth();
  const [eligibility, setEligibility] = useState<ChatEligibility>({
    hasActiveChat: false,
    chatRooms: [],
    isLoading: true,
    error: null
  });

  const fetchChatRooms = async () => {
    try {
      setEligibility(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Vérifier que l'utilisateur est authentifié et que le token est configuré
      if (!user || !token) {
        console.log('User not authenticated, skipping chat rooms fetch');
        setEligibility({
          hasActiveChat: false,
          chatRooms: [],
          isLoading: false,
          error: null
        });
        return;
      }
      
      // Vérifier que le token est bien configuré dans axios
      if (!axios.defaults.headers.common['Authorization']) {
        console.log('Token not configured in axios, waiting...');
        // Attendre un peu que le token soit configuré
        setTimeout(() => {
          if (axios.defaults.headers.common['Authorization']) {
            fetchChatRooms();
          }
        }, 100);
        return;
      }
      
      const response = await axios.get('/api/chat/rooms');
      const chatRooms = response.data || [];
      
      setEligibility({
        hasActiveChat: chatRooms.length > 0,
        chatRooms,
        isLoading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching chat rooms:', error);
      setEligibility({
        hasActiveChat: false,
        chatRooms: [],
        isLoading: false,
        error: 'Erreur lors du chargement des conversations'
      });
    }
  };

  useEffect(() => {
    if (user && token) {
      // Ajouter un petit délai pour s'assurer que le token est configuré dans axios
      const timeoutId = setTimeout(() => {
        fetchChatRooms();
      }, 200);
      
      // Rafraîchir toutes les 30 secondes
      const interval = setInterval(fetchChatRooms, 30000);
      
      return () => {
        clearTimeout(timeoutId);
        clearInterval(interval);
      };
    } else {
      console.log('User not authenticated, clearing chat eligibility');
      setEligibility({
        hasActiveChat: false,
        chatRooms: [],
        isLoading: false,
        error: null
      });
    }
  }, [user, token]);

  return {
    hasActiveChat: eligibility.hasActiveChat,
    chatRooms: eligibility.chatRooms,
    isLoading: eligibility.isLoading,
    error: eligibility.error,
    refreshEligibility: fetchChatRooms
  };
};
